/**
 * Pure helpers for the sidebar's multi-select state machine. Separated from
 * the React component so the range/toggle math is unit-testable and so the
 * tree component itself stays focused on rendering + side effects.
 *
 * The model:
 *   - `selected: Set<string>` is the canonical selection (membership is by
 *     absolute path).
 *   - `anchor: string | null` is the last "primary" pick — Shift-click
 *     extends the selection from this anchor through the clicked row.
 *
 * The reducer-style functions return *new* objects (the caller commits via
 * setState).
 */

export interface SelectionState {
  selected: ReadonlySet<string>;
  /** Path of the anchor for Shift-range selections. Null = no anchor yet. */
  anchor: string | null;
}

export const EMPTY_SELECTION: SelectionState = {
  selected: new Set(),
  anchor: null,
};

/**
 * Plain click: replace the selection with a single row. The anchor advances
 * to the clicked row so a subsequent Shift-click extends from here.
 */
export function selectOnly(path: string): SelectionState {
  return { selected: new Set([path]), anchor: path };
}

/**
 * Cmd/Ctrl-click: toggle one row's membership. The anchor moves to the
 * clicked row only when adding (matches Finder's behaviour — toggling-off
 * doesn't change where Shift-range extends from).
 */
export function toggleOne(state: SelectionState, path: string): SelectionState {
  const next = new Set(state.selected);
  if (next.has(path)) {
    next.delete(path);
    return { selected: next, anchor: state.anchor };
  }
  next.add(path);
  return { selected: next, anchor: path };
}

/**
 * Shift-click: REPLACE the selection with every visible row from the anchor
 * (inclusive) through `path` (inclusive). When there's no anchor yet, this
 * acts like a plain click.
 *
 * `orderedPaths` is the *visible* flattened-tree order; range slicing is by
 * index in that array. Rows hidden behind collapsed folders are not in the
 * selection by construction.
 */
export function selectRange(
  state: SelectionState,
  path: string,
  orderedPaths: readonly string[],
): SelectionState {
  if (state.anchor == null) return selectOnly(path);
  const anchorIdx = orderedPaths.indexOf(state.anchor);
  const clickIdx = orderedPaths.indexOf(path);
  if (anchorIdx < 0 || clickIdx < 0) return selectOnly(path);
  const [lo, hi] = anchorIdx <= clickIdx ? [anchorIdx, clickIdx] : [clickIdx, anchorIdx];
  const next = new Set<string>();
  for (let i = lo; i <= hi; i++) next.add(orderedPaths[i]);
  // The anchor STAYS — Shift-clicking again from another row extends from
  // the same anchor.
  return { selected: next, anchor: state.anchor };
}

/** Clear the selection back to empty. */
export function clearSelection(): SelectionState {
  return EMPTY_SELECTION;
}

/**
 * Drop paths that are no longer in the visible tree (e.g. trashed, renamed,
 * a workspace root removed). Anchor is cleared if it falls out of scope.
 */
export function pruneSelection(
  state: SelectionState,
  validPaths: ReadonlySet<string>,
): SelectionState {
  let changed = false;
  const next = new Set<string>();
  for (const p of state.selected) {
    if (validPaths.has(p)) next.add(p);
    else changed = true;
  }
  const anchor = state.anchor && validPaths.has(state.anchor) ? state.anchor : null;
  if (!changed && anchor === state.anchor) return state; // identity preserved
  return { selected: next, anchor };
}
